import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, CircularProgress, Collapse, Divider, Grid, IconButton, Stack, Tooltip } from "@mzc-pdc/ui";
import Typography from "@components_v2/typography/typography";
import State from "@components/state";
import {
    IconArrowDown,
    IconArrowUp,
    IconCancelCircle,
    IconCircle,
    IconDownload,
    IconErrorCircle,
    IconOpenInNew,
    IconRetry,
    ImageLogoAmazonLarge,
} from "@mzc-cloudplex/icons";
import { Trans, useTranslation } from "react-i18next";
import moment from "moment";
import { alpha } from "@mui/material";
import { capitalize } from "@utils/convert-string";
import * as jobStatus from "@cores/jobStatus";
import { Link } from "react-router-dom";
import { JOB_ACTIONS, UPLOAD_FILE_LOCATION_TYPE } from "@constants";
import StorageDownloadCdnTooltip from "@components/tooltip/StorageDownloadCdnTooltip";
import { LinearProgressDeterminate } from "@components_v2/progressbar/progressbar";
import theme from "@styles/theme";

const COLLAPSED_SIZE = 75;
const COLLAPSED_SIZE_WITH_PROGRESS = 101;

const UPLOADING_JOB_STATUSES = ["RUNNING", "UPLOADING", "PROGRESSING"];

/**
 * 속도 산출 구간. 창 안의 전송량을 그 구간의 실제 전송 시간으로 나눈다.
 *
 * 진행 이벤트가 보고하는 것은 회선으로 나간 바이트가 아니라 소켓이 받아준 바이트다.
 * 이벤트 간격은 거의 일정한데(실측 335ms) 그 사이 받아주는 양은 크게 흔들려
 * (실측 0.82~4.75MB, 변동계수 0.48) 구간을 합산해야 평균으로 수렴한다.
 * 지수이동평균도 검토했으나 이상치가 섞여 들어와 표시 변화폭이 2.6 배 컸다.
 */
const SPEED_WINDOW_MS = 5000;

/**
 * 구간이 이보다 짧으면 값을 내지 않는다.
 *
 * ⚠️ presign 대기를 걸러내려는 값이 **아니다** — 그건 uploadActiveMs 가 처리한다.
 * 남은 목적은 버스트 보고 노이즈의 평균화다. 표본 하나짜리 순간 전송률은
 * 실측에서 정상값의 0.3~2 배로 흩어진다(activeMs 가 정확한데도).
 *
 * 낮춰도 얻는 게 없다 — 표본 2 개가 모이는 730ms 가 하한이라 첫 표시는 안 당겨지고,
 * 첫 값만 더 낮게 뜬다.
 */
const SPEED_MIN_SPAN_MS = 1000;

/**
 * 멈춤 재평가 주기. 진행 이벤트가 끊기면 속도 memo 가 스스로 다시 돌지 않으므로
 * 타이머가 이 주기로 재평가를 밀어 준다. 최악 감지 지연 ≈ SPEED_WINDOW_MS + 이 값.
 */
const STALL_CHECK_MS = 1000;

const formatUploadSpeed = (bytesPerSecond) => {
    const mbitPerSecond = (bytesPerSecond * 8) / 1000 / 1000;
    if (Number(mbitPerSecond.toFixed(1)) >= 10) return `${Math.round(mbitPerSecond)}Mb/s`;
    if (Number(mbitPerSecond.toFixed(2)) >= 1) return `${mbitPerSecond.toFixed(1)}Mb/s`;
    const small = mbitPerSecond.toFixed(2);
    return Number(small) > 0 ? `${small}Mb/s` : null;
};

const JobItem = ({ job, task, action }) => {
    const { t } = useTranslation();

    const getSummaryValueById = useCallback(({ id, label, value, to }) => {
        switch (id) {
            case "service": {
                const icon = value.serviceComponent?.provider === "AWS" ? <ImageLogoAmazonLarge /> : null;
                const provider = value.serviceComponent?.service === "MediaConvert" ? "AWS Elemental MediaConvert" : "";

                return (
                    <>
                        {icon}
                        {provider}
                    </>
                );
            }
            case "text":
                return value ? t(`common::label::${value}`, value) : "-";
            case "link":
                return to ? (
                    <Typography color={"primary.main"}>
                        <Link href="#" target={"_blank"} to={to} underline="hover" rel="noreferrer">
                            {value ?? "-"}
                            <IconOpenInNew size={16} />
                        </Link>
                    </Typography>
                ) : (
                    value ?? "-"
                );
            case "date":
                return `${moment.utc(value).local().format("YYYY-MM-DD HH:mm:ss")}`;
            default:
                return value ?? "-";
        }
    }, []);

    const isJobFailed = useMemo(() => job.status === "FAILED" || job.isPartialFailure, [job]);

    /**
     * 자산 생성 잡의 로컬 파일 진행률 — 바이트 가중 `Σloaded / Σsize`.
     * 파일별 %의 단순 평균은 크기가 다른 파일이 섞이면 크게 어긋난다
     * (4GB 1% + 40MB 100% → 가중 1.96%, 평균 50.5%).
     *
     * loaded 판정 순서: uploadFinished → uploadedSize → size x pct/100(근사).
     * 완료 dispatch 는 바이트를 안 보내 uploadedSize 가 직전 값에 머무므로,
     * uploadFinished 를 먼저 보지 않으면 완료된 파일이 99.9% 로 굳는다.
     *
     * 분모는 file.size 다 — uploadTotalSize 는 첫 진행 이벤트 뒤에야 생겨 도중에 바뀐다.
     */
    const uploadProgress = useMemo(() => {
        if (job.action !== JOB_ACTIONS.UPLOAD_ASSET) return null;

        const localFiles = (task?.data ?? [])
            .flatMap((taskItem) => taskItem?.files ?? [])
            .filter((file) => file?.locationType !== UPLOAD_FILE_LOCATION_TYPE.S3 && file?.size > 0);

        if (localFiles.length === 0) return null;

        const totalBytes = localFiles.reduce((acc, file) => acc + file.size, 0);

        // 속도 지표는 태스크 단위로 모은다. 시계를 태스크가 공유해 같은 태스크의 파일들은
        // uploadActiveMs 가 동일하므로, 파일별로 나누면 파일 수만큼 중복된다.
        let loadedBytes = 0;
        const transfers = (task?.data ?? []).map((taskItem, taskIndex) => {
            let measuredBytes = 0;
            let taskActiveMs = 0;
            let taskId = null;

            (taskItem?.files ?? []).forEach((file) => {
                if (file?.locationType === UPLOAD_FILE_LOCATION_TYPE.S3 || !(file?.size > 0)) return;

                // 창 양 끝 표본을 짝짓는 신원. 자산 이름은 비거나 중복일 수 있고,
                // 배열 인덱스는 큐가 prepend 라 그룹이 늦게 추가되면 밀린다. 파일 uniqid 를 쓴다.
                if (taskId === null) taskId = file.id ?? null;

                // 표시용 — 완료된 파일은 크기로 채운다.
                if (file.uploadFinished) loadedBytes += file.size;
                else if (file.uploadedSize > 0) loadedBytes += Math.min(file.uploadedSize, file.size);
                else loadedBytes += (file.size * Math.min(file.uploadedPercentage ?? 0, 100)) / 100;

                // 측정용 — 보고된 바이트만 쓴다. 완료 보정까지 더하면 분자만 늘어
                // 마지막에 속도가 튄다(완료 dispatch 는 activeMs 를 안 보낸다).
                measuredBytes += Math.min(file.uploadedSize ?? 0, file.size);
                taskActiveMs = Math.max(taskActiveMs, file.uploadActiveMs ?? 0);
            });

            return { id: taskId ?? `index-${taskIndex}`, loaded: measuredBytes, activeMs: taskActiveMs };
        });

        return {
            value: Math.min((loadedBytes / totalBytes) * 100, 100),
            loadedBytes,
            transfers,
        };
    }, [job.action, task?.data]);

    // (시각, 누적 바이트) 표본. 렌더 중에 갱신하지만 loaded 가 변했을 때만 쌓으므로
    // 같은 내용으로 다시 그려져도 표본이 늘지 않는다.
    const speedSamplesRef = useRef([]);

    // 멈춤 판정 기준 시각. 전송이 끊기면 리렌더가 없어 아래 memo 가 스스로 안 돌기 때문에
    // 타이머가 이 값을 밀어 재평가시킨다.
    const [stallCheckAt, setStallCheckAt] = useState(() => Date.now());

    const uploadSpeedLabel = useMemo(() => {
        const samples = speedSamplesRef.current;

        if (uploadProgress == null) {
            samples.length = 0;
            return null;
        }

        const now = Date.now();
        const loaded = uploadProgress.loadedBytes;

        // 아직 한 바이트도 안 갔으면 표본을 쌓지 않는다. presign 대기 중의 관측을 시작점으로
        // 쓰면 그 대기가 분모에 들어간다. 아래 낡은 표본 리셋은 presign 이 창보다 짧으면
        // 발동하지 않아 이 가드가 따로 필요하다.
        if (loaded === 0) {
            samples.length = 0;
            return null;
        }

        const latest = samples[samples.length - 1];

        if (latest != null) {
            // 누적이 줄면 전송이 다시 시작된 것이고(재시도·파트 재시도), 직전 표본이 창보다
            // 오래됐으면 그 사이 전송이 없었던 것이다. 어느 쪽이든 시작점으로 쓸 수 없다.
            //
            // 이 판정이 멈춤 감지도 겸한다 — 표본이 비워지면 아래 `length < 2` 로 값이 사라진다.
            // stallCheckAt 은 값으로는 no-op 이고, 타이머가 재평가를 유발하는 용도다.
            if (loaded < latest.loaded || Math.max(now, stallCheckAt) - latest.at > SPEED_WINDOW_MS)
                samples.length = 0;
        }

        if (samples.length === 0 || samples[samples.length - 1].loaded !== loaded) {
            samples.push({ at: now, loaded, transfers: uploadProgress.transfers });
        }

        // 창 밖 표본은 조건 없이 버린다(양 끝은 남긴다). 개수 유지를 창보다 우선하면
        // 이벤트가 드물 때 창이 넓어져 옛 값에 굳는다.
        while (samples.length > 2 && now - samples[1].at > SPEED_WINDOW_MS) samples.shift();

        if (samples.length < 2) return null;

        const oldest = samples[0];
        const newest = samples[samples.length - 1];

        const spanMs = newest.at - oldest.at;
        if (spanMs < SPEED_MIN_SPAN_MS) return null;

        const deltaBytes = newest.loaded - oldest.loaded;
        if (deltaBytes <= 0) return null;

        // 태스크별로 (전송 바이트 ÷ 실제 전송 시간) 을 구해 더한다. 미디어 타입 그룹은 병렬이라
        // 처리량은 합쳐지지만 시간은 겹쳐서 합쳐지지 않기 때문이다. 태스크 안의 파일은 순차라
        // 공유 시계가 이미 합집합을 쌓아 뒀다.
        const previousById = new Map(oldest.transfers.map((item) => [item.id, item]));
        let bytesPerSecond = 0;
        newest.transfers.forEach((current) => {
            const previous = previousById.get(current.id);
            if (previous == null) return;
            const activeMs = current.activeMs - previous.activeMs;
            const bytes = current.loaded - previous.loaded;
            if (activeMs <= 0 || bytes <= 0) return;
            bytesPerSecond += bytes / (activeMs / 1000);
        });

        // 전송 시간을 안 싣는 경로에서는 벽시계로 되돌아간다.
        if (bytesPerSecond <= 0) return formatUploadSpeed(deltaBytes / (spanMs / 1000));

        return formatUploadSpeed(bytesPerSecond);
    }, [uploadProgress, stallCheckAt]);

    const showUploadSpeed = useMemo(
        () => uploadSpeedLabel != null && UPLOADING_JOB_STATUSES.includes(job.status),
        [uploadSpeedLabel, job.status],
    );

    useEffect(() => {
        if (!showUploadSpeed) return undefined;

        const timerId = setInterval(() => setStallCheckAt(Date.now()), STALL_CHECK_MS);
        return () => clearInterval(timerId);
    }, [showUploadSpeed]);

    const showUploadProgress = useMemo(
        () =>
            uploadProgress != null &&
            uploadProgress.loadedBytes > 0 &&
            task?.isTaskDetailFolded &&
            UPLOADING_JOB_STATUSES.includes(job.status),
        [uploadProgress, job.status, task?.isTaskDetailFolded],
    );

    const renderJobStatus = useCallback(() => {
        const action = job.action.toLowerCase().split("_").join(" ");

        const getJobRunningStatusByAction = (action) => {
            switch (action) {
                case JOB_ACTIONS.CREATE_ASSETS:
                    return "Creating asset";
                // 이 case 가 없으면 default 의 `Preparing {{type}}` 로 떨어진다.
                // {{type}} 접두어 형태는 완료·오류·취소 문구와 맞춘 것이라 유지한다.
                case JOB_ACTIONS.UPLOAD_ASSET:
                    return "{{type}} in progress";
                case JOB_ACTIONS.TRANSCODE:
                    return "Transcode in progress";
                case JOB_ACTIONS.UPLOAD:
                    return "Uploading files";
                default:
                    return "Preparing {{type}}";
            }
        };

        switch (job.status) {
            case "READY":
                return <Typography>{t(`common::label::Ready`, `Ready`)}</Typography>;
            case "INGESTING":
                return (
                    <>
                        <CircularProgress size={12} thickness={5} color="warning" />
                        <Typography color="warning.main">{t(`common::label::Ingesting`, `Ingesting`)}</Typography>
                    </>
                );
            case "INGESTED":
                return (
                    <>
                        <CircularProgress size={12} thickness={5} color="warning" />
                        <Typography color="warning.main">{t(`common::label::Ingested`, `Ingested`)}</Typography>
                    </>
                );
            case "SUBMITTED":
                return (
                    <>
                        <CircularProgress size={12} thickness={5} color="primary" />
                        <Typography color="primary.main">{t(`common::label::Submitted`, `Submitted`)}</Typography>
                    </>
                );
            case "UPLOADING":
            case "PROGRESSING":
            case "RUNNING": {
                const status = getJobRunningStatusByAction(job.action);
                return (
                    <>
                        <CircularProgress size={12} thickness={5} color="primary" />
                        <Typography color="primary.main">
                            {t(`common::label::${status}`, status, { type: t(`common::label::${action}`, action) })}
                        </Typography>
                    </>
                );
            }
            case "COMPLETE":
            case "SUCCEED":
                return (
                    <Typography color={job.isPartialFailure ? "error.main" : "success.main"}>
                        {job.isPartialFailure
                            ? t(`common::label::${"Partial {{type}} to completed."}`, {
                                  type: t(`common::label::${action}`, action),
                              })
                            : t(`common::label::${"{{type}} completed."}`, {
                                  type: t(`common::label::${capitalize(action, true)}`, capitalize(action, true)),
                              })}
                    </Typography>
                );
            case "ERROR":
            case "FAILED":
                return (
                    <Typography color="error.main">
                        {t(
                            `common::label::An error occurred while preparing {{type}}.`,
                            `An error occurred while preparing {{type}}.`,
                            {
                                type: t(`common::label::${action}`, action),
                            },
                        )}
                    </Typography>
                );
            case "CANCELED":
                return <Typography>{t(`common::label::${action} canceled`, `${action} canceled`)}</Typography>;
            default:
                return (
                    <Typography color="primary.main">
                        {action === JOB_ACTIONS.MOVE
                            ? `${t(`common::label::${"Moving"}`)}...`
                            : action === JOB_ACTIONS.COPY
                            ? `${t(`common::label::${"Copying to"}`)}...`
                            : action === JOB_ACTIONS.DELETE
                            ? `${t(`common::label::${"Deleting"}`)}...`
                            : action === JOB_ACTIONS.RENAME
                            ? `${t(`common::label::${"Renaming"}`)}...`
                            : ""}
                    </Typography>
                );
        }
    }, [job]);

    return (
        <Box>
            <Grid container pb={1} gap={1} alignItems={"center"}>
                <Grid item>
                    <Grid
                        container
                        alignItems={"center"}
                        justifyContent={"center"}
                        width={24}
                        height={20}
                        bgcolor={theme.palette.etc.gray500}
                        color={theme.palette.constant.white}
                        borderRadius={0.7}
                    >
                        {job.icon}
                    </Grid>
                </Grid>
                <Divider flexItem orientation={"vertical"} sx={{ my: 1 }} />
                <Grid
                    item
                    xs
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Grid container gap={1} alignItems={"center"} flex={1}>
                        <State variant={"contained"} status={job.status} />
                        {isJobFailed && (
                            <Tooltip
                                title={
                                    job?.exitCode !== "SUCCESS"
                                        ? t(`common::code::${job?.exitCode}`, job?.exitCode)
                                        : t(
                                              `common::label::This operation was not entirely successful.`,
                                              "This operation was not entirely successful.",
                                          )
                                }
                            >
                                <IconButton circled size="small">
                                    <IconErrorCircle size={16} color="error" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Grid>
                    <Grid item>
                        {action.cancel && !jobStatus.isComplete(job.status) && (
                            <Button
                                variant={"text"}
                                size={"medium"}
                                color={"greySecondary"}
                                startIcon={<IconCancelCircle size={16} />}
                                onClick={() => action.cancel()}
                            >
                                {t(`common::label::Cancel`, `Cancel`)}
                            </Button>
                        )}
                    </Grid>
                </Grid>
                <Grid item>
                    {isJobFailed && action.retry && (
                        <Button
                            variant={"text"}
                            size={"medium"}
                            color={"greySecondary"}
                            startIcon={<IconRetry size={16} />}
                            onClick={() => action.retry()}
                        >
                            {t(`common::label::Retry`, `Retry`)}
                        </Button>
                    )}
                </Grid>
            </Grid>
            <Divider />
            <Stack py={2.5} px={1.5} gap={1.5}>
                {job?.summaryInfo?.map((info, index) => (
                    <Grid container gap={1} key={`job-item-${job.id}-${index}`}>
                        {info.label && info.value && (
                            <>
                                <Grid item xs={4}>
                                    <Grid container gap={1} alignItems={"center"}>
                                        <IconCircle size={6} htmlColor={theme.palette.greySecondary[500]} />
                                        {t(`common::label::${info.label}`, info.label)}
                                    </Grid>
                                </Grid>
                                <Grid item>{getSummaryValueById(info)}</Grid>
                            </>
                        )}
                    </Grid>
                ))}
                <Grid item></Grid>
            </Stack>
            {/*isFolded를 상태로 관리하면? (transcode나 upload같은 경우도 굳이 리덕스에 저장 안해도 됨) */}
            <Collapse
                in={!task.isTaskDetailFolded}
                collapsedSize={showUploadProgress ? COLLAPSED_SIZE_WITH_PROGRESS : COLLAPSED_SIZE}
            >
                <Box
                    px={1}
                    py={1.5}
                    borderRadius={1}
                    bgcolor={alpha(theme.palette.greySecondary[50], 0.5)}
                    maxHeight={500}
                    overflow={"auto"}
                >
                    <Grid container gap={1.5}>
                        <Grid item>
                            <Tooltip
                                title={t(
                                    `common::label::${task.isTaskDetailFolded ? "Expand" : "Fold"}`,
                                    task.isTaskDetailFolded ? "Expand" : "Fold",
                                )}
                            >
                                <IconButton
                                    circled
                                    size="xxsmall"
                                    onClick={() => action?.toggle && action.toggle()}
                                    disabled={task.taskCount === 0}
                                    sx={{ width: 20, height: 20 }}
                                >
                                    {task.isTaskDetailFolded ? <IconArrowDown size={12} /> : <IconArrowUp size={12} />}
                                </IconButton>
                            </Tooltip>
                        </Grid>
                        <Grid item xs pr={1}>
                            <Stack gap={1.5}>
                                <Grid container flexDirection={"column"}>
                                    <Stack direction={"row"} sx={{ justifyContent: "space-between" }}>
                                        <Box mt={0.25} mb={1.5} alignItems={"baseline"}>
                                            <Trans
                                                i18nKey="common::msg::Detailed view of <0> {{number}} </0> tasks"
                                                values={{ number: task.taskCount || 0 }}
                                                components={[
                                                    <Typography
                                                        key="highlight"
                                                        sx={{ ml: theme.spacing(0.5) }}
                                                        variant={"body3_700"}
                                                        color="primary.main"
                                                    ></Typography>,
                                                ]}
                                            />
                                        </Box>
                                        {action.download && job.status === "SUCCEED" && (
                                            <Box>
                                                <Button
                                                    size="small"
                                                    color="greySecondary"
                                                    startIcon={<IconDownload size="16" />}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        action.download();
                                                    }}
                                                >
                                                    {t(
                                                        `common::label::Download${job.fileCount > 1 ? " All" : ""}`,
                                                        `Download${job.fileCount > 1 ? " All" : ""}`,
                                                    )}
                                                </Button>
                                            </Box>
                                        )}
                                    </Stack>
                                    <Grid
                                        container
                                        gap={0.75}
                                        alignItems={"center"}
                                        flexWrap={"nowrap"}
                                        sx={{
                                            "& > *": {
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            },
                                        }}
                                    >
                                        {job.showCdnIcon && <StorageDownloadCdnTooltip />}
                                        {renderJobStatus()}
                                        {showUploadSpeed && (
                                            <Typography
                                                variant={"body2_700"}
                                                color={"primary"}
                                                sx={{ flexShrink: 0, ml: "auto", pl: 1 }}
                                            >
                                                {uploadSpeedLabel}
                                            </Typography>
                                        )}
                                    </Grid>
                                    {showUploadProgress && (
                                        <Grid container alignItems={"center"} mt={0.75}>
                                            <LinearProgressDeterminate
                                                value={uploadProgress.value}
                                                withLabel={true}
                                                sx={{
                                                    height: 8,
                                                    borderRadius: "4px",
                                                    "& .MuiLinearProgress-bar": { height: 8, borderRadius: "4px" },
                                                }}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </Stack>
                            {!task.isTaskDetailFolded && (
                                <>
                                    <task.taskDetailComponent
                                        tasks={task.data}
                                        metadata={task.metadata}
                                        job={task.job}
                                        action={task.action}
                                    />
                                    <Box
                                        sx={{
                                            borderRadius: "12px",
                                            px: theme.spacing(1),
                                            backgroundColor: alpha(theme.palette.greySecondary[50], 0.8),
                                            width: "fit-content",
                                            my: theme.spacing(0.75),
                                        }}
                                    >
                                        <Typography
                                            variant={"body3_400"}
                                            color={theme.palette.greySecondary[500]}
                                        >{`ID ${job.id}`}</Typography>
                                    </Box>
                                </>
                            )}
                        </Grid>
                    </Grid>
                </Box>
            </Collapse>
        </Box>
    );
};

export default JobItem;
